import React, { useCallback } from 'react';

import { UIDAPresentationTypeEnum } from '@kaltura-ott/tvpil';
import { UserActionType } from '@kaltura-ott/tvpil-analytics';
import { Channel, RhinoProgram } from '@kaltura-ott/tvpil-shared';
import { ParentalPinStoreFacade } from '@kaltura-ott/tvpil-shared/lib/facades/parental-pin-store-facade';

import { DescriptionWithAge } from 'components/widgets/Modal/PinCodeModal/DescriptionWithAge/DescriptionWithAge';
import PinCodeModal from 'components/widgets/Modal/PinCodeModal/PinCodeModal';
import { PinCodeModalTypes } from 'components/widgets/Modal/PinCodeModal/PinCodeModalTypesEnum';
import { useRailContext } from 'components/widgets/Rail/components/RailProvider/RailProvider';
import { LAST_SCROLL_Y_POSITION } from 'consts';
import { useModalContext } from 'context/modal/modal';
import { useStableReactRouter } from 'context/stableReactRouterContext/stableReactRouterContext';
import { useIsUrlToFullPlayer, useSettingsStore } from 'hooks';
import { globalStorage } from 'services/globalStorage';
import { resolvePathToDetailsPage, userActionEvents } from 'utils';

interface Props {
  channelId: number;
  data: Channel | RhinoProgram;
  link: string;
  mediaTypeParam: string;
  imageHeight: number;
  isPurchasable: boolean;
  collectionType: string | undefined;
  mosaicExternalId?: string;
  isChannelHasNoProgram: boolean;
  isRestricted: boolean;
  parentalPinStore: ParentalPinStoreFacade;
}

export const useClickHandler = ({
  channelId,
  data,
  link,
  mediaTypeParam,
  imageHeight,
  isPurchasable,
  collectionType,
  isChannelHasNoProgram,
  mosaicExternalId,
  isRestricted,
  parentalPinStore,
}: Props) => {
  const { onCardClick } = useRailContext();
  const { navigate } = useStableReactRouter();
  const { settingsStore } = useSettingsStore();
  const isUrlToFullPlayer = useIsUrlToFullPlayer(link);
  const modalContext = useModalContext();
  // needs for parental rating check in case of url to full player and parental rating is blocked
  const parentalRating = Number(data?.parentalRating);

  return useCallback(
    (e: React.MouseEvent<any>) => {
      if (isChannelHasNoProgram) {
        e.preventDefault();

        return;
      }

      globalStorage.clearDetailsAsset(); // Temporary solution to avoid opening a detail page with an asset saved in globalStorage

      sessionStorage.setItem(LAST_SCROLL_Y_POSITION, window.scrollY.toString());

      if (onCardClick) {
        onCardClick(channelId);
      }

      if (isRestricted) {
        e.preventDefault();
        e.stopPropagation();

        const validateAdult = (pin: string) => parentalPinStore.validateAdultPin(pin);

        modalContext.openModal(
          <PinCodeModal
            context={UIDAPresentationTypeEnum.applications}
            type={PinCodeModalTypes.RestrictedContent}
            validatePin={validateAdult}
          />,
        );

        return;
      }

      if (isPurchasable) {
        e.preventDefault();

        userActionEvents(UserActionType.details, data?.id, mediaTypeParam);

        navigate(
          resolvePathToDetailsPage({
            id: data?.id!,
            mediaTypeParam: mediaTypeParam.toLowerCase(),
            subtype: collectionType,
            externalId: mosaicExternalId ?? '',
          }),
          {
            state: {
              lastScrollYPosition: window.scrollY + imageHeight,
            },
          },
        );
        return;
      }

      if (isUrlToFullPlayer && parentalRating && parentalPinStore.isBlockedByParentalRating(parentalRating)) {
        e.preventDefault();

        const validateParental = (pin: string) => parentalPinStore.validateParentalRating({ pin, parentalRating });

        modalContext.openModal(
          <PinCodeModal
            callback={(success: boolean) => {
              if (success) {
                navigate(link);
              }
            }}
            context={UIDAPresentationTypeEnum.applications}
            description={parentalRating && <DescriptionWithAge age={parentalRating} />}
            type={PinCodeModalTypes.RestrictedContentParental}
            validatePin={validateParental}
          />,
        );
        return;
      }

      if (link.includes('http')) {
        e.preventDefault();
        window.open(link, '_blank');
      }

      if (link.includes('details')) {
        userActionEvents(UserActionType.details, data?.id, mediaTypeParam);
      }
    },
    [modalContext, link, parentalRating, settingsStore, isChannelHasNoProgram, isRestricted],
  );
};
